/**
 * Supabase Client Configuration
 * Configured identically to Hub for SSO session sharing
 * Uses cookieOptions with domain: '.domio.com.pl' to share session with Hub
 * 
 * CRITICAL: storageKey: 'domio-auth-token' forces Supabase to use this exact cookie name
 * instead of the default 'sb-{project-ref}-auth-token' format.
 * This MUST be identical in every application under .domio.com.pl domain.
 */

import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@/integrations/supabase/types';
import type { Session } from '@supabase/supabase-js';
import {
  createTimedAuthFetch,
  installForegroundAuthRecovery,
  noOpAuthLock,
  REFRESH_SESSION_TIMEOUT_MS,
  WRITE_OPERATION_TIMEOUT_MS,
} from '@/lib/supabaseNetwork';
import { TIMEOUT_MESSAGE, withTimeout } from '@/lib/with-timeout';

export type AppSupabaseClient = SupabaseClient<Database, "public">;

type DomioAuthOptions = {
  storageKey: string;
  persistSession: boolean;
  autoRefreshToken: boolean;
  detectSessionInUrl: boolean;
  flowType: "pkce";
  lock: typeof noOpAuthLock;
  stopAutoRefreshOnTabBlur: boolean;
  cookieOptions: {
    domain: string;
    path: string;
    sameSite: "Lax" | "Strict" | "None";
    secure: boolean;
  };
};

/**
 * Universal Session Bridge
 * 
 * Automatically detects and initializes Supabase session from any available source:
 * - Searches all cookies for keys containing "-auth-token" (e.g., "domio-auth-token", "sb-...-auth-token")
 * - Searches localStorage for the same keys
 * - Extracts session data and uses supabase.auth.setSession() to initialize session
 * 
 * SECURITY: This mechanism only "hands over" found tokens to Supabase's official setSession() method,
 * which performs cryptographic verification. Invalid tokens are automatically rejected.
 * 
 * @param client - Supabase client instance
 * @returns Promise<boolean> - true if session was successfully initialized, false otherwise
 */
async function universalSessionBridge(client: AppSupabaseClient): Promise<boolean> {
  try {
    console.log('[UniversalSessionBridge] Rozpoczynam wykrywanie sesji...');

    // Step 1: Search all cookies for "-auth-token" keys
    const cookieAuthTokens: string[] = [];
    if (typeof document !== 'undefined' && document.cookie) {
      const cookies = document.cookie.split('; ').filter(Boolean);
      for (const cookie of cookies) {
        const [key, value] = cookie.split('=');
        if (key && key.includes('-auth-token') && value) {
          cookieAuthTokens.push(value);
          console.log('[UniversalSessionBridge] Znaleziono token w ciasteczkach:', key);
        }
      }
    }

    // Step 2: Search localStorage for "-auth-token" keys
    const localStorageAuthTokens: string[] = [];
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.includes('-auth-token')) {
            const value = localStorage.getItem(key);
            if (value) {
              localStorageAuthTokens.push(value);
              console.log('[UniversalSessionBridge] Znaleziono token w localStorage:', key);
            }
          }
        }
      } catch (error) {
        console.warn('[UniversalSessionBridge] Błąd dostępu do localStorage:', error);
      }
    }

    // Combine all found tokens
    const allTokens = [...cookieAuthTokens, ...localStorageAuthTokens];
    
    if (allTokens.length === 0) {
      console.log('[UniversalSessionBridge] Nie znaleziono żadnych tokenów auth');
      return false;
    }

    console.log(`[UniversalSessionBridge] Znaleziono ${allTokens.length} token(ów), próba inicjalizacji sesji...`);

    // Step 3: Try to parse and set session from each token
    // Tokens are typically stored as JSON-encoded session objects
    for (const tokenValue of allTokens) {
      try {
        // Try to parse as JSON (Supabase stores sessions as JSON)
        let sessionData: any;
        try {
          // Handle both plain JSON strings and URL-encoded values
          const decodedValue = decodeURIComponent(tokenValue);
          sessionData = JSON.parse(decodedValue);
        } catch {
          // If parsing fails, try direct parsing
          try {
            sessionData = JSON.parse(tokenValue);
          } catch {
            // If still fails, it might be a plain token string
            // In this case, we can't use it directly with setSession()
            console.warn('[UniversalSessionBridge] Nie można sparsować tokena jako JSON, pomijam...');
            continue;
          }
        }

        // Verify that sessionData has required structure
        if (sessionData && typeof sessionData === 'object') {
          // Check if it's a session object (has access_token or user)
          if (sessionData.access_token || sessionData.user) {
            console.log('[UniversalSessionBridge] Próba ustawienia sesji z wykrytego tokena...');
            
            // Use setSession() which performs cryptographic verification
            const { data, error } = await client.auth.setSession({
              access_token: sessionData.access_token || '',
              refresh_token: sessionData.refresh_token || '',
            });

            if (error) {
              console.warn('[UniversalSessionBridge] Błąd ustawiania sesji:', error.message);
              
              // If token is invalid, try to clean it up
              if (error.message.includes('invalid') || error.message.includes('expired')) {
                console.log('[UniversalSessionBridge] Token nieważny, próba czyszczenia...');
                
                // Clean up cookies
                if (typeof document !== 'undefined') {
                  const cookies = document.cookie.split('; ');
                  for (const cookie of cookies) {
                    const [key] = cookie.split('=');
                    if (key && key.includes('-auth-token')) {
                      // Delete cookie by setting it to expire in the past
                      document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.domio.com.pl;`;
                      document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
                      console.log('[UniversalSessionBridge] Usunięto nieważne ciasteczko:', key);
                    }
                  }
                }
                
                // Clean up localStorage
                if (typeof window !== 'undefined' && window.localStorage) {
                  try {
                    for (let i = localStorage.length - 1; i >= 0; i--) {
                      const key = localStorage.key(i);
                      if (key && key.includes('-auth-token')) {
                        localStorage.removeItem(key);
                        console.log('[UniversalSessionBridge] Usunięto nieważny token z localStorage:', key);
                      }
                    }
                  } catch (error) {
                    console.warn('[UniversalSessionBridge] Błąd czyszczenia localStorage:', error);
                  }
                }
              }
              continue; // Try next token
            }

            if (data.session) {
              console.log('[UniversalSessionBridge] Sesja pomyślnie ustawiona z wykrytego tokena:', {
                userId: data.session.user?.id?.substring(0, 8) + '...',
                expiresAt: data.session.expires_at,
              });
              return true; // Successfully initialized session
            }
          } else {
            console.warn('[UniversalSessionBridge] Wykryty token nie zawiera wymaganych pól (access_token/user)');
          }
        }
      } catch (error) {
        console.warn('[UniversalSessionBridge] Błąd przetwarzania tokena:', error);
        continue; // Try next token
      }
    }

    console.log('[UniversalSessionBridge] Nie udało się zainicjalizować sesji z żadnego wykrytego tokena');
    return false;
  } catch (error) {
    console.error('[UniversalSessionBridge] Wyjątek podczas wykrywania sesji:', error);
    return false;
  }
}

/**
 * Initialize Universal Session Bridge
 * This function should be called immediately after creating the Supabase client
 * to automatically detect and initialize sessions from cookies/localStorage
 * 
 * @param client - Supabase client instance
 * @param onSuccess - Optional callback called when session is successfully initialized
 */
export async function initUniversalSessionBridge(
  client: AppSupabaseClient,
  onSuccess?: (session: Session) => void
): Promise<void> {
  try {
    const success = await universalSessionBridge(client);
    
    if (success) {
      // Get the session that was just set
      const { data: { session } } = await client.auth.getSession();
      
      if (session && onSuccess) {
        onSuccess(session);
      }
      
      // Integrate with tenant resolver - resolve tenant after session is set
      if (session) {
        console.log('[UniversalSessionBridge] Sesja ustawiona, inicjalizacja tenant resolvera...');
        // Import tenant resolver dynamically to avoid circular dependencies
        const { resolveTenant } = await import('./tenant-resolver');
        
        try {
          const tenantResult = await resolveTenant();
          if (tenantResult.success && tenantResult.tenantInfo) {
            console.log('[UniversalSessionBridge] Tenant rozwiązany:', {
              tenantId: tenantResult.tenantInfo.tenantId,
              orgId: tenantResult.tenantInfo.orgId,
            });
          } else {
            console.warn('[UniversalSessionBridge] Nie udało się rozwiązać tenant:', tenantResult.error);
          }
        } catch (error) {
          console.warn('[UniversalSessionBridge] Błąd podczas rozwiązywania tenant:', error);
        }
      }
    } else {
      // CRITICAL: Even if bridge doesn't find tokens, we must signal completion
      // This prevents deadlock where app waits forever for bridge initialization
      console.log('[UniversalSessionBridge] Nie znaleziono tokenów auth - mostek zakończył pracę bez sesji');
    }
  } catch (error) {
    console.error('[UniversalSessionBridge] Błąd inicjalizacji mostu sesji:', error);
    // CRITICAL: Even on error, bridge initialization is considered complete
    // This prevents deadlock where app waits forever for bridge initialization
  }
}

/**
 * Redirect to login when session expires (401) or user signs out.
 * Uses Hub URL for SSO - consistent with LandingPage.
 */
export function redirectToLogin(): void {
  const hubUrl = 'https://domio.com.pl'; // Hub SSO – zgodnie z LandingPage
  const returnTo = encodeURIComponent(window.location.href);
  window.location.href = `${hubUrl}/login?returnTo=${returnTo}`;
}

/** Forced first-login PIN/password change on Hub, then back to this Cleaning origin. */
export function redirectToHubChangePassword(): void {
  const host = window.location.hostname;
  let hubOrigin = 'https://domio.com.pl';
  if (host.includes('localhost') || host.includes('127.0.0.1')) {
    const port = window.location.port ? `:${window.location.port}` : '';
    hubOrigin = `http://localhost${port}`;
  }
  const returnTo = encodeURIComponent(`${window.location.origin}/`);
  window.location.href = `${hubOrigin}/change-password?returnTo=${returnTo}`;
}

/** Temporary PIN/password change: simplified workers and fleet drivers only. */
export function shouldForcePasswordChange(profile: {
  is_first_login?: boolean | null;
  account_type?: string | null;
  fleet_role?: string | null;
} | null | undefined): boolean {
  if (!profile || profile.is_first_login !== true) return false;
  const accountType = (profile.account_type ?? '').trim().toLowerCase();
  const fleetRole = (profile.fleet_role ?? '').trim().toLowerCase();
  if (accountType === 'simplified') return true;
  if (fleetRole === 'driver') return true;
  return false;
}

/**
 * Supabase client configured identically to Hub
 * Uses cookieOptions with domain: '.domio.com.pl', sameSite: 'Lax' to share session
 * Uses storageKey: 'domio-auth-token' - MUST be identical in both apps (Hub and Cleaning)
 * 
 * This configuration enables the app to read session cookies created by Hub
 * on the main domain (domio.com.pl) and automatically authenticate users.
 */
/**
 * Second generic MUST be the schema name ("public").
 * Passing only <Database> plus a client-options object makes TypeScript treat
 * those options as SchemaNameOrClientOptions and breaks .eq() / select inference.
 */
export const supabase = createClient<Database, "public">(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY,
  {
    auth: {
      storageKey: 'domio-auth-token',
      persistSession: true,
      autoRefreshToken: true,
      stopAutoRefreshOnTabBlur: true,
      lock: noOpAuthLock,
      detectSessionInUrl: true,
      flowType: 'pkce',
      cookieOptions: {
        domain: '.domio.com.pl',
        path: '/',
        sameSite: 'Lax',
        secure: true,
      },
    } as DomioAuthOptions,
    global: {
      headers: {
        'X-Client-Info': 'domio-cleaning',
      },
      fetch: createTimedAuthFetch(redirectToLogin),
    },
  }
);

installForegroundAuthRecovery(supabase);

// NOTE: Universal Session Bridge is now initialized in main.tsx BEFORE app rendering
// This ensures session is detected and set before React components mount
// Do NOT call initUniversalSessionBridge() here - it's handled in main.tsx

export interface RetryOptions {
  errorMessage?: string;
  /** Default: false. Read: plain await. Write: retry with session heal. */
  isReadOperation?: boolean;
}

/**
 * Aggressive Retry Strategy
 * - Read: timeout so a zombie connection cannot spin the UI forever
 * - Write: timeout → refresh session + 500ms cooldown → retry
 */
export const executeWithRetry = async <T>(
  operation: () => PromiseLike<T>,
  options: RetryOptions = {}
): Promise<T> => {
  const { errorMessage = "Błąd połączenia.", isReadOperation = false } = options;

  if (isReadOperation) {
    return await withTimeout(operation(), WRITE_OPERATION_TIMEOUT_MS);
  }

  try {
    return await withTimeout(operation(), WRITE_OPERATION_TIMEOUT_MS);
  } catch (err) {
    console.warn("[RetryEngine] Błąd zapisu. Próba naprawy sesji...", err);

    try {
      const result = await withTimeout(
        supabase.auth.refreshSession(),
        REFRESH_SESSION_TIMEOUT_MS
      );
      if (result?.error) throw result.error;

      await new Promise((r) => setTimeout(r, 500));
    } catch (refreshErr) {
      console.error("[RetryEngine] Naprawa sesji nieudana.", refreshErr);
      throw new Error("Błąd połączenia. Skopiuj dane i odśwież stronę ręcznie.");
    }

    try {
      console.log("[RetryEngine] Ponawiam akcję...");
      return await withTimeout(operation(), WRITE_OPERATION_TIMEOUT_MS);
    } catch (retryErr) {
      const message =
        retryErr instanceof Error && retryErr.message === TIMEOUT_MESSAGE
          ? TIMEOUT_MESSAGE
          : errorMessage + " (Spróbuj odświeżyć stronę).";
      throw new Error(message);
    }
  }
};
